import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SearchIcon from '@mui/icons-material/Search';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import moment from 'moment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { HotelDateTimePicker as DateTimePicker } from '../../../shared/components/forms/HotelDatePicker';
import {
  createHallBookingVendorAssignment,
  getHallVendors,
  removeHallBookingVendorPayment,
  updateHallBookingVendorAssignment,
} from '../api';
import { HALL_EVENT_PAYMENT_METHOD_OPTIONS } from '../utils/hallEventForm';
import { getHallApiErrorMessage } from '../utils/hallErrors';
import { formatHallCurrency, roundHallMoney } from '../utils/hallFinancials';
import {
  getHallDatePickerSlotProps,
  getHallDrawerPaperSx,
  getHallFormInputSx,
} from '../utils/hallDrawerStyles';
import {
  HALL_VENDOR_SETTLEMENT_OPTIONS,
  HallVendorSettlementChips,
  HallVendorSettlementTiles,
} from './HallVendorSettlementSummary';

const HALL_ACCENT = '#4ce1b6';

function humanizeToken(value) {
  return String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export default function HallAddVendorDrawer({
  open,
  onClose,
  bookingId,
  booking,
  isDark,
  availableVendors = [],
  assignedVendors = [],
  editingAssignment = null,
  onSaved,
}) {
  const inputSx = getHallFormInputSx(isDark);
  const isEdit = Boolean(editingAssignment?.id);
  const assignedVendorIds = useMemo(
    () => new Set((assignedVendors || []).map((row) => Number(row.vendor_id || row.id)).filter(Boolean)),
    [assignedVendors],
  );

  const [vendorOptions, setVendorOptions] = useState([]);
  const [vendorSearch, setVendorSearch] = useState('');
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [task, setTask] = useState('');
  const [notes, setNotes] = useState('');
  const [contractAmount, setContractAmount] = useState('');
  const [includeInHall, setIncludeInHall] = useState(true);
  /** due = leave unpaid; partial = pay some now; full = settle contract now */
  const [settlement, setSettlement] = useState('due');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [mode, setMode] = useState('cash');
  const [paidAt, setPaidAt] = useState(() => moment());
  const [reference, setReference] = useState('');
  const [paymentNote, setPaymentNote] = useState('');
  const [recordedPayments, setRecordedPayments] = useState([]);
  const [paymentToRemove, setPaymentToRemove] = useState(null);
  const [removingPaymentId, setRemovingPaymentId] = useState(null);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const contractValue = roundHallMoney(contractAmount);
  const alreadyPaid = isEdit
    ? roundHallMoney(
      recordedPayments.reduce((sum, row) => sum + (Number(row.amount) || 0), 0),
    )
    : 0;
  const outstandingDue = Math.max(0, roundHallMoney(contractValue - alreadyPaid));
  const payValue = settlement === 'full'
    ? outstandingDue
    : roundHallMoney(paymentAmount);
  const remainingDue = Math.max(0, roundHallMoney(outstandingDue - (
    settlement === 'due' ? 0 : payValue
  )));
  const canRecordPayout = outstandingDue > 0.009;
  const settlementMeta = HALL_VENDOR_SETTLEMENT_OPTIONS.find((row) => row.value === settlement);
  const settlementHelp = isEdit
    ? (settlementMeta?.helpEdit || '')
    : (settlementMeta?.helpCreate || '');

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      const editVendor = {
        id: editingAssignment.vendor_id,
        name: editingAssignment.name,
        vendor_type: editingAssignment.vendor_type,
        vendor_type_label: editingAssignment.vendor_type_label,
        phone: editingAssignment.phone,
        email: editingAssignment.email,
      };
      setSelectedVendor(editVendor);
      setVendorSearch(editVendor.name || '');
      setTask(editingAssignment.task || '');
      setNotes(editingAssignment.notes || '');
      setContractAmount(
        editingAssignment.contract_amount != null ? String(editingAssignment.contract_amount) : '',
      );
      setIncludeInHall(editingAssignment.include_in_hall !== false);
      setSettlement('due');
      setRecordedPayments(
        Array.isArray(editingAssignment.payments) ? editingAssignment.payments : [],
      );
    } else {
      setSelectedVendor(null);
      setVendorSearch('');
      setTask('');
      setNotes('');
      setContractAmount('');
      setIncludeInHall(true);
      setSettlement('due');
      setRecordedPayments([]);
    }
    setPaymentAmount('');
    setMode('cash');
    setPaidAt(moment());
    setReference('');
    setPaymentNote('');
    setError('');
    setSubmitting(false);
    setPaymentToRemove(null);
    setRemovingPaymentId(null);
    setVendorOptions(Array.isArray(availableVendors) ? availableVendors : []);
    // Reset only when the drawer opens / edit target changes — not when availableVendors refreshes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, editingAssignment?.id]);

  useEffect(() => {
    if (!open || !isEdit || !editingAssignment) return;
    setRecordedPayments(
      Array.isArray(editingAssignment.payments) ? editingAssignment.payments : [],
    );
  }, [open, isEdit, editingAssignment]);

  useEffect(() => {
    if (!open) return;
    if (settlement === 'full' && outstandingDue > 0) {
      setPaymentAmount(String(outstandingDue));
    }
    if (settlement === 'due') {
      setPaymentAmount('');
    }
  }, [open, settlement, outstandingDue]);

  useEffect(() => {
    if (!open || isEdit) return undefined;
    // Keep the selected vendor label in the input; don't re-query while a vendor is chosen
    // unless the user is actively typing a new search.
    const query = vendorSearch.trim();
    if (selectedVendor?.name && query === String(selectedVendor.name).trim()) {
      return undefined;
    }
    let active = true;
    const timer = window.setTimeout(async () => {
      setLoadingOptions(true);
      try {
        const response = await getHallVendors(query ? { search: query } : {});
        if (!active) return;
        const results = Array.isArray(response?.results) ? response.results : [];
        // Ensure the currently selected vendor stays in options so Autocomplete can render it.
        if (
          selectedVendor?.id
          && !results.some((row) => Number(row.id) === Number(selectedVendor.id))
        ) {
          setVendorOptions([selectedVendor, ...results]);
        } else {
          setVendorOptions(results);
        }
      } catch (err) {
        if (!active) return;
        setError(getHallApiErrorMessage(err, 'Failed to load vendors'));
      } finally {
        if (active) setLoadingOptions(false);
      }
    }, 250);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [open, isEdit, vendorSearch, selectedVendor]);

  const selectableOptions = useMemo(() => {
    const rows = Array.isArray(vendorOptions) ? vendorOptions : [];
    const filtered = isEdit
      ? rows
      : rows.filter((row) => (
        !assignedVendorIds.has(Number(row.id))
        || Number(row.id) === Number(selectedVendor?.id)
      ));
    if (
      selectedVendor?.id
      && !filtered.some((row) => Number(row.id) === Number(selectedVendor.id))
    ) {
      return [selectedVendor, ...filtered];
    }
    return filtered;
  }, [assignedVendorIds, isEdit, selectedVendor, vendorOptions]);

  const syncPaymentsFromPayload = (result) => {
    const rows = Array.isArray(result?.assigned_vendors) ? result.assigned_vendors : [];
    const match = rows.find((row) => Number(row.id) === Number(editingAssignment?.id));
    if (match) {
      setRecordedPayments(Array.isArray(match.payments) ? match.payments : []);
    }
  };

  const closeRemovePaymentDialog = () => {
    if (removingPaymentId) return;
    setPaymentToRemove(null);
  };

  const handleConfirmRemovePayment = async () => {
    const payment = paymentToRemove;
    if (!bookingId || !editingAssignment?.id || !payment?.id) return;

    setRemovingPaymentId(payment.id);
    setError('');
    try {
      const result = await removeHallBookingVendorPayment(
        bookingId,
        editingAssignment.id,
        payment.id,
      );
      syncPaymentsFromPayload(result);
      if (settlement !== 'due') setSettlement('due');
      setPaymentToRemove(null);
      onSaved?.(result);
    } catch (err) {
      setError(getHallApiErrorMessage(err, 'Failed to remove vendor payout'));
    } finally {
      setRemovingPaymentId(null);
    }
  };

  const handleSubmit = async () => {
    if (!bookingId) {
      setError('Booking is missing.');
      return;
    }
    if (!isEdit && !selectedVendor?.id) {
      setError('Select a vendor.');
      return;
    }
    if (contractValue < 0) {
      setError('Contract amount cannot be negative.');
      return;
    }
    if (isEdit && contractValue + 0.009 < alreadyPaid) {
      setError(`Contract cannot be less than already paid (${formatHallCurrency(alreadyPaid)}).`);
      return;
    }
    if ((settlement === 'partial' || settlement === 'full') && outstandingDue <= 0) {
      setError('There is no outstanding vendor due to pay.');
      return;
    }
    if (settlement === 'partial') {
      if (payValue <= 0) {
        setError('Enter a partial payout greater than zero, or choose Due.');
        return;
      }
      if (payValue >= outstandingDue) {
        setError('Partial payout must be less than the remaining due. Use Full to settle completely.');
        return;
      }
    }
    if (settlement === 'full' && payValue <= 0) {
      setError('Full settlement requires an outstanding due greater than zero.');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const basePayload = {
        task: String(task || '').trim(),
        notes: String(notes || '').trim(),
        contract_amount: contractValue,
        include_in_hall: Boolean(includeInHall),
      };
      const paymentPayload = {
        amount: payValue,
        mode,
        payment_type: settlement === 'full'
          ? 'full'
          : (alreadyPaid > 0 ? 'partial' : 'advance'),
        paid_at: paidAt?.isValid?.() ? paidAt.toISOString() : moment().toISOString(),
        reference: String(reference || '').trim(),
        note: String(paymentNote || '').trim(),
      };
      let result;
      if (isEdit) {
        const payload = { ...basePayload };
        if (settlement === 'partial' || settlement === 'full') {
          payload.additional_payment = paymentPayload;
        }
        result = await updateHallBookingVendorAssignment(
          bookingId,
          editingAssignment.id,
          payload,
        );
      } else {
        const payload = {
          vendor_id: selectedVendor.id,
          ...basePayload,
        };
        if (settlement === 'partial' || settlement === 'full') {
          payload.initial_payment = paymentPayload;
        }
        result = await createHallBookingVendorAssignment(bookingId, payload);
      }
      onSaved?.(result);
      onClose?.();
    } catch (err) {
      setError(getHallApiErrorMessage(err, 'Failed to save vendor'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={submitting || removingPaymentId ? undefined : onClose}
      PaperProps={{
        sx: getHallDrawerPaperSx(isDark),
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative', overflow: 'hidden' }}>
        <Box
          sx={{
            px: 2.25,
            py: 1.75,
            borderBottom: `1px solid ${isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.08)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1 }}>
            <LocalShippingIcon sx={{ color: HALL_ACCENT }} />
            <Box>
              <Typography sx={{ fontWeight: 800, color: isDark ? '#f8fafc' : '#0f172a' }}>
                {isEdit ? 'Edit Vendor Assignment' : 'Add Vendor'}
              </Typography>
              <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 600 }}>
                {booking?.event_name || 'Hall event'} · {booking?.booking_code || booking?.event_id || ''}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} disabled={submitting} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ px: 2.25, py: 2, flex: 1, overflow: 'auto' }}>
          <Stack spacing={1.75}>
            <Autocomplete
              options={selectableOptions}
              loading={loadingOptions}
              value={selectedVendor}
              disabled={isEdit}
              onChange={(event, value) => {
                setSelectedVendor(value);
                setVendorSearch(value?.name || '');
              }}
              inputValue={vendorSearch}
              onInputChange={(event, value, reason) => {
                // Controlled inputValue must track selection resets, or the field stays empty.
                if (reason === 'reset') {
                  setVendorSearch(value || selectedVendor?.name || '');
                  return;
                }
                if (reason === 'clear') {
                  setSelectedVendor(null);
                  setVendorSearch('');
                  return;
                }
                setVendorSearch(value);
                if (
                  reason === 'input'
                  && selectedVendor
                  && value.trim() !== String(selectedVendor.name || '').trim()
                ) {
                  setSelectedVendor(null);
                }
              }}
              getOptionLabel={(option) => option?.name || ''}
              isOptionEqualToValue={(option, value) => Number(option?.id) === Number(value?.id)}
              noOptionsText={vendorSearch.trim() ? 'No matching vendors' : 'No vendors available'}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700 }}>{option.name}</Typography>
                    <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                      {[
                        option.vendor_type_label || humanizeToken(option.vendor_type),
                        option.phone,
                        option.email,
                      ].filter(Boolean).join(' · ')}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Vendor"
                  placeholder="Search by name, phone, or type"
                  sx={inputSx}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <SearchIcon sx={{ color: isDark ? '#94a3b8' : '#64748b', mr: 0.75 }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <TextField
              label="Assigned task"
              value={task}
              onChange={(event) => setTask(event.target.value)}
              placeholder="e.g. Stage décor, catering for 300 guests"
              fullWidth
              multiline
              minRows={2}
              sx={inputSx}
            />

            <TextField
              label="Contract amount"
              type="number"
              value={contractAmount}
              onChange={(event) => setContractAmount(event.target.value)}
              fullWidth
              sx={inputSx}
              InputProps={{
                startAdornment: <InputAdornment position="start">INR</InputAdornment>,
              }}
              helperText="Total agreed amount for this vendor on the event"
            />

            <Box
              sx={{
                p: 1.35,
                borderRadius: 2,
                border: `1px solid ${isDark ? alpha(HALL_ACCENT, 0.28) : alpha(HALL_ACCENT, 0.32)}`,
                backgroundColor: isDark ? alpha('#0f172a', 0.45) : alpha(HALL_ACCENT, 0.08),
              }}
            >
              <FormControlLabel
                control={(
                  <Switch
                    checked={includeInHall}
                    onChange={(event) => setIncludeInHall(event.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: HALL_ACCENT },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: HALL_ACCENT,
                      },
                    }}
                  />
                )}
                label={(
                  <Box>
                    <Typography sx={{ fontWeight: 800, color: isDark ? '#f8fafc' : '#0f172a', fontSize: '0.92rem' }}>
                      {includeInHall ? 'Include in hall collection' : 'Exclude from hall collection'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 600 }}>
                      {includeInHall
                        ? 'Contract amount is added to the guest hall bill / grand total.'
                        : 'Tracked for vendor ops only — not added to the guest invoice.'}
                    </Typography>
                  </Box>
                )}
                sx={{ alignItems: 'flex-start', m: 0, gap: 0.5 }}
              />
            </Box>

            <TextField
              label="Notes"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              fullWidth
              multiline
              minRows={2}
              sx={inputSx}
            />

            <>
              <Divider sx={{ borderColor: isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.08) }} />

              {isEdit ? (
                <>
                  <HallVendorSettlementTiles
                    contractAmount={contractValue}
                    paidAmount={alreadyPaid}
                    dueAmount={outstandingDue}
                    isDark={isDark}
                  />

                  {recordedPayments.length ? (
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 700, display: 'block', mb: 0.75 }}
                      >
                        Recorded payouts
                      </Typography>
                      <Stack spacing={0.75}>
                        {recordedPayments.map((payment) => (
                          <Box
                            key={payment.id}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 1,
                              px: 1.1,
                              py: 0.85,
                              borderRadius: 1.5,
                              border: `1px solid ${isDark ? alpha('#64748b', 0.35) : alpha('#cbd5e1', 0.7)}`,
                              backgroundColor: isDark ? alpha('#0f172a', 0.35) : alpha('#fff', 0.95),
                            }}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontWeight: 800, color: isDark ? '#f8fafc' : '#0f172a', fontSize: '0.86rem' }}>
                                {formatHallCurrency(payment.amount)}
                                {payment.payment_type_label || payment.payment_type
                                  ? ` · ${payment.payment_type_label || String(payment.payment_type).replace(/_/g, ' ')}`
                                  : ''}
                              </Typography>
                              <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 600 }}>
                                {String(payment.mode || payment.payment_method || 'cash').replace(/_/g, ' ')}
                                {payment.paid_at || payment.processed_at
                                  ? ` · ${moment(payment.paid_at || payment.processed_at).format('DD MMM YYYY, hh:mm A')}`
                                  : ''}
                              </Typography>
                            </Box>
                            <Button
                              size="small"
                              variant="text"
                              disabled={submitting || removingPaymentId === payment.id}
                              onClick={() => setPaymentToRemove(payment)}
                              sx={{
                                textTransform: 'none',
                                fontWeight: 700,
                                minWidth: 0,
                                color: isDark ? '#fca5a5' : '#b91c1c',
                              }}
                            >
                              {removingPaymentId === payment.id ? 'Removing...' : 'Remove'}
                            </Button>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  ) : (
                    <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 600 }}>
                      No payouts recorded yet for this vendor.
                    </Typography>
                  )}
                </>
              ) : null}

              <HallVendorSettlementChips
                value={settlement}
                onChange={setSettlement}
                isDark={isDark}
                canRecordPayout={canRecordPayout}
                helpText={
                  !canRecordPayout && isEdit
                    ? 'This vendor is fully paid. You can still update task, contract, and notes.'
                    : settlementHelp
                }
              />

              {settlement === 'due' ? (
                <Box
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    border: `1px solid ${isDark ? alpha('#f59e0b', 0.35) : alpha('#f59e0b', 0.4)}`,
                    backgroundColor: isDark ? alpha('#f59e0b', 0.12) : alpha('#f59e0b', 0.08),
                  }}
                >
                  <Typography variant="caption" sx={{ color: isDark ? '#fde68a' : '#92400e', fontWeight: 700 }}>
                    Vendor due after save: {formatHallCurrency(outstandingDue)}
                    {settlement === 'due'
                      ? ' · no new row written to vendor payments.'
                      : ''}
                  </Typography>
                </Box>
              ) : null}

              {(settlement === 'partial' || settlement === 'full') && canRecordPayout ? (
                <Stack spacing={1.5}>
                  {settlement === 'partial' ? (
                    <TextField
                      label="Amount paid now"
                      type="number"
                      value={paymentAmount}
                      onChange={(event) => setPaymentAmount(event.target.value)}
                      fullWidth
                      sx={inputSx}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">INR</InputAdornment>,
                      }}
                      helperText={
                        outstandingDue > 0
                          ? `Must be less than remaining due ${formatHallCurrency(outstandingDue)}`
                          : 'No outstanding due'
                      }
                    />
                  ) : (
                    <Box
                      sx={{
                        p: 1.25,
                        borderRadius: 2,
                        border: `1px solid ${isDark ? alpha(HALL_ACCENT, 0.35) : alpha(HALL_ACCENT, 0.4)}`,
                        backgroundColor: isDark ? alpha(HALL_ACCENT, 0.1) : alpha(HALL_ACCENT, 0.08),
                      }}
                    >
                      <Typography variant="caption" sx={{ color: isDark ? '#bbf7d0' : '#166534', fontWeight: 700 }}>
                        {isEdit
                          ? `Paying remaining due: ${formatHallCurrency(outstandingDue)}`
                          : `Paying full contract: ${formatHallCurrency(contractValue)}`}
                      </Typography>
                    </Box>
                  )}

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 1,
                      px: 0.25,
                    }}
                  >
                    <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 700 }}>
                      Remaining due after payout
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 800,
                        color: remainingDue > 0
                          ? (isDark ? '#ffb74d' : '#e65100')
                          : (isDark ? '#86efac' : '#2e7d32'),
                      }}
                    >
                      {formatHallCurrency(remainingDue)}
                    </Typography>
                  </Box>

                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DateTimePicker
                      label="Paid at"
                      value={paidAt}
                      onChange={(value) => setPaidAt(value)}
                      slotProps={getHallDatePickerSlotProps(isDark, { fullWidth: true, sx: inputSx })}
                    />
                  </LocalizationProvider>
                  <Box>
                    <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 700, mb: 0.75, display: 'block' }}>
                      Payment mode
                    </Typography>
                    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                      {HALL_EVENT_PAYMENT_METHOD_OPTIONS.map((option) => {
                        const selected = mode === option.value;
                        return (
                          <Chip
                            key={option.value}
                            label={option.label}
                            onClick={() => setMode(option.value)}
                            sx={{
                              fontWeight: 700,
                              cursor: 'pointer',
                              color: selected ? '#04120e' : (isDark ? '#cbd5e1' : '#334155'),
                              bgcolor: selected
                                ? HALL_ACCENT
                                : (isDark ? alpha('#94a3b8', 0.14) : alpha('#64748b', 0.1)),
                            }}
                          />
                        );
                      })}
                    </Stack>
                  </Box>
                  <TextField
                    label="Reference / txn id"
                    value={reference}
                    onChange={(event) => setReference(event.target.value)}
                    fullWidth
                    sx={inputSx}
                  />
                  <TextField
                    label="Payment note"
                    value={paymentNote}
                    onChange={(event) => setPaymentNote(event.target.value)}
                    fullWidth
                    sx={inputSx}
                  />
                </Stack>
              ) : null}
            </>

            {error ? (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {String(error)}
              </Alert>
            ) : null}
          </Stack>
        </Box>

        <Box
          sx={{
            px: 2.25,
            py: 1.75,
            borderTop: `1px solid ${isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.08)}`,
            display: 'flex',
            gap: 1.25,
          }}
        >
          <Button
            fullWidth
            variant="outlined"
            onClick={onClose}
            disabled={submitting}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || Boolean(removingPaymentId)}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              backgroundColor: HALL_ACCENT,
              color: '#04120e',
              '&:hover': { backgroundColor: HALL_ACCENT, opacity: 0.94 },
            }}
          >
            {submitting ? 'Saving...' : (isEdit ? 'Save changes' : 'Add vendor')}
          </Button>
        </Box>

        {paymentToRemove ? (
          <Box
            role="presentation"
            onClick={closeRemovePaymentDialog}
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 2,
              backgroundColor: isDark ? alpha('#020617', 0.58) : alpha('#0f172a', 0.28),
              backdropFilter: 'blur(2px)',
            }}
          >
            <Paper
              elevation={0}
              role="dialog"
              aria-labelledby="remove-vendor-payout-title"
              onClick={(event) => event.stopPropagation()}
              sx={{
                width: '100%',
                maxWidth: 360,
                borderRadius: 3,
                overflow: 'hidden',
                background: isDark
                  ? `linear-gradient(165deg, #111827 0%, #0f1c2e 55%, ${alpha('#7f1d1d', 0.35)} 100%)`
                  : `linear-gradient(165deg, #ffffff 0%, #fff7f7 55%, ${alpha('#fee2e2', 0.65)} 100%)`,
                border: `1px solid ${isDark ? alpha('#f87171', 0.28) : alpha('#ef4444', 0.18)}`,
                boxShadow: isDark
                  ? `0 18px 36px ${alpha('#000', 0.4)}`
                  : `0 16px 32px ${alpha('#7f1d1d', 0.14)}`,
                p: 2.25,
              }}
            >
              <Stack spacing={1.5} alignItems="center" textAlign="center">
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    bgcolor: alpha('#ef4444', isDark ? 0.22 : 0.12),
                    color: isDark ? '#fca5a5' : '#dc2626',
                    border: `1px solid ${alpha('#ef4444', isDark ? 0.4 : 0.25)}`,
                  }}
                >
                  <UndoRoundedIcon />
                </Avatar>
                <Box>
                  <Typography
                    id="remove-vendor-payout-title"
                    sx={{ fontWeight: 900, color: isDark ? '#f8fafc' : '#0f172a', fontSize: '1.02rem' }}
                  >
                    Remove this payout?
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mt: 0.55, color: isDark ? '#94a3b8' : '#64748b', fontWeight: 600, lineHeight: 1.45 }}
                  >
                    The amount returns to vendor due and is logged in event audit.
                  </Typography>
                </Box>

                <Box
                  sx={{
                    width: '100%',
                    px: 1.4,
                    py: 1.1,
                    borderRadius: 2,
                    border: `1px solid ${isDark ? alpha('#f87171', 0.28) : alpha('#ef4444', 0.18)}`,
                    backgroundColor: isDark ? alpha('#0f172a', 0.55) : alpha('#fff', 0.85),
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 900,
                      fontSize: '1.15rem',
                      color: isDark ? '#fecaca' : '#b91c1c',
                      lineHeight: 1.2,
                    }}
                  >
                    {formatHallCurrency(paymentToRemove?.amount)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 700 }}>
                    {[
                      paymentToRemove?.payment_type_label
                        || String(paymentToRemove?.payment_type || '').replace(/_/g, ' ')
                        || 'Payout',
                      String(paymentToRemove?.mode || paymentToRemove?.payment_method || 'cash').replace(/_/g, ' '),
                      paymentToRemove?.paid_at || paymentToRemove?.processed_at
                        ? moment(paymentToRemove.paid_at || paymentToRemove.processed_at).format('DD MMM, hh:mm A')
                        : null,
                    ].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>

                <Stack direction="row" spacing={0.85} justifyContent="center" sx={{ width: '100%', pt: 0.25 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={closeRemovePaymentDialog}
                    disabled={Boolean(removingPaymentId)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      borderRadius: 1.5,
                      minHeight: 30,
                      px: 1.4,
                      fontSize: '0.78rem',
                    }}
                  >
                    Keep
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    onClick={handleConfirmRemovePayment}
                    disabled={Boolean(removingPaymentId)}
                    startIcon={
                      removingPaymentId
                        ? <CircularProgress size={12} color="inherit" />
                        : <UndoRoundedIcon sx={{ fontSize: 15 }} />
                    }
                    sx={{
                      textTransform: 'none',
                      fontWeight: 800,
                      borderRadius: 1.5,
                      minHeight: 30,
                      px: 1.4,
                      fontSize: '0.78rem',
                      boxShadow: 'none',
                      '& .MuiButton-startIcon': { mr: 0.55 },
                      '&:hover': { boxShadow: 'none' },
                    }}
                  >
                    {removingPaymentId ? 'Removing...' : 'Remove'}
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Box>
        ) : null}
      </Box>
    </Drawer>
  );
}
